import React from "react";

import ReportFormTable, {
  DateInputCell,
  RadioGroupInputCell,
  ReportFormRow,
  TextAreaInputCell,
} from "../common/ReportTableForm";

import ReportDocuments from "../../../documentManagement/components/ReportDocuments";

import {
  DocumentEntityType,
  DocumentType,
} from "../../../../../src/api/caseDocuments";

import {
  PreSCNFicDecision,
  PreSCNFicMeetingUI,
} from "../../../../../src/api/preSCNFic";

import { User } from "../../../../../src/api/Users";
import { EventType } from "../../../../../src/api/events";
import { Users } from "../../../../../src/features/users/components/Users";

import { useAppSelector } from "../../../../../src/app/hooks";
import { selectEventByTypeAndEntityId } from "../../../../../src/features/timeline/logic/slice";
import { formatDateWithOrdinal } from "../../../../../src/lib/utils";

interface PreSCNFicMeetingSectionProps {
  meeting: PreSCNFicMeetingUI;
  onChange: (updatedMeeting: PreSCNFicMeetingUI) => void;
  conveners: User[];
  committeeMembers: User[];
  index: number;
}

const PreSCNFicMeetingSection: React.FC<
  PreSCNFicMeetingSectionProps
> = ({ meeting, onChange, conveners, committeeMembers, index }) => {

  const handleFieldChange = <K extends keyof PreSCNFicMeetingUI>(
    key: K,
    value: PreSCNFicMeetingUI[K]
  ) => {
    onChange({
      ...meeting,
      [key]: value,
    });
  };

  const isNewRow = meeting.isAdded;

  const meetingEvent = useAppSelector(
    meeting.meeting_id
      ? selectEventByTypeAndEntityId(
          EventType.FIC_REVIEW_MEETING_HELD,
          meeting.meeting_id
        )
      : () => undefined
  );

  const eventDate = meetingEvent?.date;

  const finalDate = isNewRow
    ? meeting.date_of_committee_meeting
    : eventDate;

  let tableTitle = "";

  if (isNewRow) {
    tableTitle = `${index + 1}. Untitled`;
  } else {
    const committeeName =
      meeting.name_of_committee || "Untitled";

    const formattedDate = finalDate
      ? formatDateWithOrdinal(finalDate)
      : "";

    tableTitle = `${index + 1}. ${committeeName}${
      formattedDate ? ` - ${formattedDate}` : ""
    }`;
  }

  const formRows: ReportFormRow[] = [
    {
      label: "Convener",
      inputCell: (
        <Users
          userIds={meeting.convener ? [meeting.convener] : []}
          maxVisible={1}
          deleteEnabled={isNewRow && !!meeting.convener}
          addEnabled={isNewRow && !meeting.convener}
          user_id_options={conveners.map((u) => u.user_id)}
          onChange={(ids) => {
            handleFieldChange("convener", ids[0] || "");
          }}
        />
      ),
    },
    {
      label: "Name of Committee",
      inputCell: (
        <TextAreaInputCell
          value={meeting.name_of_committee ?? ""}
          onChange={(val) =>
            handleFieldChange("name_of_committee", val)
          }
          disableResize
          rows={1}
          textAreaClassName="!min-h-0"
        />
      ),
    },
    {
      label: "Members of Committee",
      inputCell: (
        <Users
          userIds={meeting.members_of_committee || []}
          maxVisible={20}
          addEnabled={true}
          deleteEnabled={true}
          user_id_options={committeeMembers.map(
            (u) => u.user_id
          )}
          onChange={(updatedIds) => {
            handleFieldChange(
              "members_of_committee",
              updatedIds
            );
          }}
        />
      ),
    },
    {
      label: "Date of Committee",
      inputCell: (
        <DateInputCell
          value={finalDate}
          disabled={!isNewRow}
          onChange={(val) =>
            handleFieldChange(
              "date_of_committee_meeting",
              val
            )
          }
        />
      ),
    },
    {
      label: "Decision",
      inputCell: (
        <RadioGroupInputCell
          value={meeting.decision}
          onChange={(val) =>
            handleFieldChange(
              "decision",
              val as PreSCNFicDecision
            )
          }
          disabled={!isNewRow}
          options={[
            {
              label: "No Fraud",
              value: PreSCNFicDecision.NO_FRAUD,
              selectedBorderClass: "border-green-600",
              selectedBgClass: "bg-green-50",
              selectedDotClass: "bg-green-600",
            },
            {
              label: "Issue SCN",
              value: PreSCNFicDecision.ISSUE_SCN,
              selectedBorderClass: "border-yellow-600",
              selectedBgClass: "bg-yellow-50",
              selectedDotClass: "bg-yellow-600",
            },
            {
              label: "Deferred",
              value: PreSCNFicDecision.DEFERRED,
              selectedBorderClass: "border-gray-600",
              selectedBgClass: "bg-gray-50",
              selectedDotClass: "bg-gray-600",
            },
          ]}
        />
      ),
    },
    {
      label: "Remarks",
      inputCell: (
        <TextAreaInputCell
          value={meeting.remarks ?? ""}
          onChange={(val) =>
            handleFieldChange("remarks", val)
          }
          placeholder="Enter Remarks"
          disableResize
          rows={3}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col mb-6">
      <div className="text-sm font-semibold mb-2">
        {tableTitle}
      </div>

      <ReportFormTable rows={formRows} />

      {!meeting.isAdded && meeting.meeting_id && (
        <ReportDocuments
          document_type={DocumentType.COMMITTEE_REPORT}
          entityId={meeting.meeting_id}
          entityType={DocumentEntityType.PRE_SCN_FIC_MEETING}
        />
      )}
    </div>
  );
};

export default PreSCNFicMeetingSection;
